import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { ContactType } from '@zorro/clients';
import { logger, responseErrorToString } from '@zorro/common-utils';
import {
  callEndpoint,
  showErrorNotification,
  useForm,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { UseFormReturn } from 'react-hook-form';

import { getDefaultPersonFormItem } from '../PersonFormArray';
import {
  ContactsFormFields,
  getContactsSchemaObject,
  mapContactItemToEmployerContactDto,
  mapEmployerContactDtoToPersonFormItem,
} from './ContactsFormUtils';

type UseContactsForm = (
  employerId: string | undefined,
  isFinalizationMode: boolean,
  isDon?: boolean
) => {
  contactsForm: UseFormReturn<ContactsFormFields>;
  duplicateFieldName: keyof ContactsFormFields;
  onSubmit: () => Promise<boolean>;
};

export const useContactsForm: UseContactsForm = (
  employerId,
  isFinalizationMode,
  isDon = false
) => {
  const queryClient = useQueryClient();
  const { data: contacts } = useMonolithQuery({
    method: 'employersControllerGetEmployerContactsByType',
    params: employerId && [employerId],
  });

  const contactsForm = useForm<ContactsFormFields>({
    mode: 'all',
    resolver: yupResolver(getContactsSchemaObject(isFinalizationMode, isDon)),
    defaultValues: {
      onboardingContacts: contacts?.onboarding?.length
        ? mapEmployerContactDtoToPersonFormItem(contacts?.onboarding)
        : [getDefaultPersonFormItem(true)],
      legalContacts: contacts?.legal?.length
        ? mapEmployerContactDtoToPersonFormItem(contacts?.legal)
        : [getDefaultPersonFormItem()],
      hrContacts: contacts?.hr?.length
        ? mapEmployerContactDtoToPersonFormItem(contacts?.hr)
        : [getDefaultPersonFormItem()],
      financeContacts: contacts?.finance?.length
        ? mapEmployerContactDtoToPersonFormItem(contacts?.finance)
        : [getDefaultPersonFormItem()],
    },
  });
  const duplicateFieldName = isFinalizationMode
    ? 'onboardingContacts'
    : 'legalContacts';
  return {
    contactsForm,
    duplicateFieldName,
    onSubmit: async (): Promise<boolean> => {
      const data = contactsForm.getValues();
      const contacts = [
        ...mapContactItemToEmployerContactDto(
          data,
          ContactType.ONBOARDING,
          duplicateFieldName
        ),
        ...mapContactItemToEmployerContactDto(
          data,
          ContactType.LEGAL,
          duplicateFieldName
        ),
        ...mapContactItemToEmployerContactDto(
          data,
          ContactType.HR,
          duplicateFieldName
        ),
        ...mapContactItemToEmployerContactDto(
          data,
          ContactType.FINANCE,
          duplicateFieldName
        ),
      ];

      try {
        if (employerId) {
          await callEndpoint({
            method: 'employersControllerUpsertEmployerContacts',
            params: [employerId, contacts],
          });
        }

        setTimeout(async () => {
          await queryClient.invalidateQueries();
        }, 0);
        return true;
      } catch (error) {
        logger.error(error);
        showErrorNotification({ message: responseErrorToString(error) });
      }

      return false;
    },
  };
};
